import React from 'react';
import { useTranslation } from 'react-i18next';
import { User, Building2, ArrowLeft, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ImpersonationBanner - Shows when admin/employee is viewing as a customer
 * Displays the original user info and provides a button to switch back
 */
export default function ImpersonationBanner({ 
  impersonationData, 
  onEndImpersonation,
  onLogout,
  isLoading = false
}: any) {
  const { t } = useTranslation();

  if (!impersonationData?.is_impersonating) return null;

  const { original_user, customer } = impersonationData;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white shadow-lg" data-testid="impersonation-banner">
      <div className="container mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-background/20 px-3 py-1 rounded-full">
            <User className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t('impersonation.viewingAs', 'Viewing as')}:
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            <span className="font-semibold text-lg" data-testid="impersonation-customer-name">
              {customer?.company_name || 'Customer'}
            </span>
            {customer?.shortcut && (
              <span className="text-white/70 text-sm">
                ({customer.shortcut})
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-white/80 hidden sm:block">
            {t('impersonation.loggedInAs', 'Logged in as')} {original_user?.name || original_user?.email}
          </span>
          <Button
            onClick={onEndImpersonation}
            disabled={isLoading}
            variant="secondary"
            size="sm"
            className="bg-background text-orange-600 hover:bg-background/90 font-semibold gap-2"
            data-testid="end-impersonation-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {isLoading 
              ? t('impersonation.switching', 'Switching...') 
              : t('impersonation.switchBack', 'Switch Back')
            }
          </Button>
          {onLogout && (
            <Button
              onClick={onLogout}
              disabled={isLoading}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/15 hover:text-white gap-1.5"
              title={t('impersonation.logoutHint', 'Avslutt kundevisningen og logg helt ut')}
              data-testid="impersonation-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t('auth.logout', 'Logg ut')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
